import { useCallback } from 'react'
import { formsApi } from '@/lib/api/forms.api'
import { ticketsApi } from '@/lib/api/tickets.api'
import {
  areFieldHistoryValuesEqual,
  getCurrentFieldHistoryValue,
} from '@/lib/field-history/value-utils'
import {
  FieldHistoryTransaction,
  useFieldHistoryStore,
} from '@/store/field-history.store'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { UpdateFormDto } from '@scrambl/shared'
import { ModuleTypeEnum } from '@scrambl/shared'

import { useUpdateContactFieldMutation } from './mutations/useContactMutations'
import { useUpdateContractFieldMutation } from './mutations/useContractMutations'
import { useUpdateCustomCollectionEntryMutation } from './mutations/useCustomCollectionsMutations'
import { useUpdateDealFieldMutation } from './mutations/useDealMutations'
import { useUpdateEntityFieldMutation } from './mutations/useEntityMutations'
import { useUpdateEventFieldMutation } from './mutations/useEventMutations'
import { useUpdateExpenseMutation } from './mutations/useExpenseMutations'
import {
  useUpdateInventoryCategoryMutation,
  useUpdateInventoryItemFieldMutation,
  useUpdateInventoryLocationMutation,
} from './mutations/useInventoryMutations'
import { useUpdateInvoiceFieldMutation } from './mutations/useInvoiceMutations'
import { useUpdateMemberFieldMutation } from './mutations/useMemberMutations'
import { useUpdateProjectFieldMutation } from './mutations/useProjectMutations'
import { useUpdateSupplierFieldMutation } from './mutations/useSupplierMutations'
import { useUpdateTaskFieldMutation } from './mutations/useTaskMutations'
import { useUpdateTimeEntryMutation } from './mutations/useTimeEntryMutations'
import { useUpdateDocumentMutation } from './queries/useDocumentsQuery'
import { useUpdateNoteFieldMutation } from './queries/useNotesQuery'
import { queryKeys } from './query-keys'

type ReplayDirection = 'undo' | 'redo'

const conflictMessage =
  'Undo/redo skipped because this field changed after your edit.'

export function useFieldHistoryReplay() {
  const queryClient = useQueryClient()
  const undo = useFieldHistoryStore(state => state.undo)
  const redo = useFieldHistoryStore(state => state.redo)

  const projectMutation = useUpdateProjectFieldMutation()
  const taskMutation = useUpdateTaskFieldMutation()
  const entityMutation = useUpdateEntityFieldMutation()
  const contactMutation = useUpdateContactFieldMutation()
  const dealMutation = useUpdateDealFieldMutation()
  const contractMutation = useUpdateContractFieldMutation()
  const invoiceMutation = useUpdateInvoiceFieldMutation()
  const eventMutation = useUpdateEventFieldMutation()
  const supplierMutation = useUpdateSupplierFieldMutation()
  const memberMutation = useUpdateMemberFieldMutation()
  const inventoryMutation = useUpdateInventoryItemFieldMutation()
  const inventoryLocationMutation = useUpdateInventoryLocationMutation()
  const inventoryCategoryMutation = useUpdateInventoryCategoryMutation()
  const documentMutation = useUpdateDocumentMutation()
  const noteMutation = useUpdateNoteFieldMutation()
  const collectionEntryMutation = useUpdateCustomCollectionEntryMutation()
  const timeEntryMutation = useUpdateTimeEntryMutation()
  const expenseMutation = useUpdateExpenseMutation()

  const ticketMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof ticketsApi.updateTicket>[1]
    }) => ticketsApi.updateTicket(id, data),
    onSuccess: ticket => {
      queryClient.setQueryData(queryKeys.tickets.detail(ticket.id), ticket)
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all })
    },
  })

  const formMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFormDto }) =>
      formsApi.update(id, data),
    onSuccess: form => {
      queryClient.setQueryData(queryKeys.forms.detail(form.id), form)
      queryClient.invalidateQueries({ queryKey: queryKeys.forms.lists() })
    },
  })

  const applyOperation = useCallback(
    async (
      operation: FieldHistoryTransaction['operations'][number],
      direction: ReplayDirection,
    ) => {
      const value =
        direction === 'undo' ? operation.previousValue : operation.nextValue

      switch (operation.moduleType) {
        case ModuleTypeEnum.PROJECT:
          await projectMutation.mutateAsync({
            id: operation.moduleId,
            field: operation.fieldName,
            value,
          })
          return
        case ModuleTypeEnum.TASK:
          await taskMutation.mutateAsync({
            id: operation.moduleId,
            field: operation.fieldName,
            value,
          })
          return
        case ModuleTypeEnum.ENTITY:
          await entityMutation.mutateAsync({
            id: operation.moduleId,
            field: operation.fieldName,
            value,
          })
          return
        case ModuleTypeEnum.CONTACT:
          await contactMutation.mutateAsync({
            id: operation.moduleId,
            field: operation.fieldName,
            value,
          })
          return
        case ModuleTypeEnum.DEAL:
          await dealMutation.mutateAsync({
            id: operation.moduleId,
            field: operation.fieldName,
            value,
          })
          return
        case ModuleTypeEnum.LEAD:
          await contactMutation.mutateAsync({
            id: operation.moduleId,
            field: operation.fieldName,
            value,
          })
          return
        case ModuleTypeEnum.CONTRACT:
          await contractMutation.mutateAsync({
            id: operation.moduleId,
            field: operation.fieldName,
            value,
          })
          return
        case ModuleTypeEnum.INVOICE:
          await invoiceMutation.mutateAsync({
            id: operation.moduleId,
            field: operation.fieldName,
            value,
          })
          return
        case ModuleTypeEnum.EVENT:
          await eventMutation.mutateAsync({
            id: operation.moduleId,
            field: operation.fieldName,
            value,
          })
          return
        case ModuleTypeEnum.SUPPLIER:
          await supplierMutation.mutateAsync({
            id: operation.moduleId,
            field: operation.fieldName,
            value,
          })
          return
        case ModuleTypeEnum.MEMBER:
          await memberMutation.mutateAsync({
            id: operation.moduleId,
            field: operation.fieldName,
            value,
          })
          return
        case ModuleTypeEnum.INVENTORY:
          await inventoryMutation.mutateAsync({
            id: operation.moduleId,
            field: operation.fieldName,
            value,
          })
          return
        case ModuleTypeEnum.INVENTORY_LOCATION:
          await inventoryLocationMutation.mutateAsync({
            id: operation.moduleId,
            data: { [operation.fieldName]: value },
          })
          return
        case ModuleTypeEnum.INVENTORY_CATEGORY:
          await inventoryCategoryMutation.mutateAsync({
            id: operation.moduleId,
            data: { [operation.fieldName]: value },
          })
          return
        case ModuleTypeEnum.DOCUMENT:
          await documentMutation.mutateAsync({
            id: operation.moduleId,
            data: { [operation.fieldName]: value },
          })
          return
        case ModuleTypeEnum.NOTE:
          await noteMutation.mutateAsync({
            id: operation.moduleId,
            field: operation.fieldName,
            value,
          })
          return
        case ModuleTypeEnum.CUSTOM_COLLECTION:
          if (!operation.context?.collectionId) {
            throw new Error('Collection context is missing for this undo item.')
          }
          await collectionEntryMutation.mutateAsync({
            collectionId: operation.context.collectionId,
            entryId: operation.moduleId,
            data: { [operation.fieldName]: value },
          })
          return
        case ModuleTypeEnum.TIME_ENTRY:
          await timeEntryMutation.mutateAsync({
            id: operation.moduleId,
            data: { [operation.fieldName]: value },
          })
          return
        case ModuleTypeEnum.EXPENSE:
          await expenseMutation.mutateAsync({
            id: operation.moduleId,
            data: { [operation.fieldName]: value },
          })
          return
        case ModuleTypeEnum.TICKET:
          await ticketMutation.mutateAsync({
            id: operation.moduleId,
            data: { [operation.fieldName]: value },
          })
          return
        case ModuleTypeEnum.FORM:
          await formMutation.mutateAsync({
            id: operation.moduleId,
            data: { [operation.fieldName]: value } as UpdateFormDto,
          })
          return
        default:
          throw new Error(`Undo is not supported for ${operation.moduleType}.`)
      }
    },
    [
      collectionEntryMutation,
      contactMutation,
      inventoryCategoryMutation,
      inventoryLocationMutation,
      contractMutation,
      dealMutation,
      documentMutation,
      entityMutation,
      eventMutation,
      expenseMutation,
      formMutation,
      inventoryMutation,
      invoiceMutation,
      memberMutation,
      noteMutation,
      projectMutation,
      supplierMutation,
      taskMutation,
      ticketMutation,
      timeEntryMutation,
    ],
  )

  const replayTransaction = useCallback(
    async (
      transaction: FieldHistoryTransaction,
      direction: ReplayDirection,
    ) => {
      const operations =
        direction === 'undo'
          ? [...transaction.operations].reverse()
          : transaction.operations

      for (const operation of operations) {
        const expectedValue =
          direction === 'undo' ? operation.nextValue : operation.previousValue
        const currentValue = getCurrentFieldHistoryValue(queryClient, operation)

        if (!areFieldHistoryValuesEqual(currentValue, expectedValue)) {
          toast.error(conflictMessage)
          throw new Error(conflictMessage)
        }
      }

      for (const operation of operations) {
        await applyOperation(operation, direction)
      }
    },
    [applyOperation, queryClient],
  )

  const runUndo = useCallback(async () => {
    try {
      await undo(replayTransaction)
    } catch (error) {
      if ((error as Error).message !== conflictMessage) {
        toast.error((error as Error).message || 'Undo failed.')
      }
    }
  }, [replayTransaction, undo])

  const runRedo = useCallback(async () => {
    try {
      await redo(replayTransaction)
    } catch (error) {
      if ((error as Error).message !== conflictMessage) {
        toast.error((error as Error).message || 'Redo failed.')
      }
    }
  }, [redo, replayTransaction])

  return {
    undo: runUndo,
    redo: runRedo,
  }
}
